import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, QrCode, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

export function PaymentQRModal({ 
  isOpen, 
  onClose, 
  orderData,
  onPaymentSuccess,
  onPaymentFailed 
}) {
  const [qrData, setQrData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [timeLeft, setTimeLeft] = useState(null);

  const generateQRCode = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://trx2.hytale-id.net';
      const response = await fetch(`${apiUrl}/api/payment/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_name: orderData.productName,
          amount: orderData.amount,
          username: orderData.username,
          group: orderData.group,
          purchase_type: orderData.purchase_type || 'rank',
        }),
      });

      if (!response.ok) {
        throw new Error('Gagal membuat transaksi pembayaran');
      }

      const data = await response.json();
      setQrData(data.payment);
      setPaymentStatus('pending');
    } catch (err) {
      console.error('QR Generation Error:', err);
      setError(err.message || 'Terjadi kesalahan saat membuat QR Code');
      setPaymentStatus('failed');
    } finally {
      setIsLoading(false);
    }
  }, [orderData]);

  const checkPaymentStatus = useCallback(async () => {
    if (!qrData?.order_id) return;

    try {
      setPaymentStatus('checking');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://trx2.hytale-id.net';
      const response = await fetch(
        `${apiUrl}/api/payment/status/${qrData.order_id}`
      );

      if (!response.ok) {
        throw new Error('Gagal memeriksa status pembayaran');
      }

      const data = await response.json();

      if (data.status === 'completed') {
        setPaymentStatus('completed');
        setTimeout(() => {
          onPaymentSuccess({
            order_id: qrData.order_id,
            product_name: orderData.productName,
            amount: orderData.amount,
            username: orderData.username,
            completed_at: data.completed_at,
          });
        }, 1500);
      } else {
        setPaymentStatus('pending');
      }
    } catch (err) {
      console.error('Payment Status Check Error:', err);
      setPaymentStatus('pending');
    }
  }, [qrData, orderData, onPaymentSuccess]);

  useEffect(() => {
    if (isOpen && orderData) {
      generateQRCode();
    }
  }, [isOpen, orderData, generateQRCode]);

  useEffect(() => {
    if (qrData?.expired_at) {
      const updateTimer = () => {
        const now = new Date().getTime();
        const expiry = new Date(qrData.expired_at).getTime();
        const diff = expiry - now;

        if (diff <= 0) {
          setTimeLeft('Expired');
          setPaymentStatus('failed');
        } else {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [qrData]);

  useEffect(() => {
    if (paymentStatus === 'pending' && qrData?.order_id) {
      const pollInterval = setInterval(async () => {
        await checkPaymentStatus();
      }, 5000);

      return () => clearInterval(pollInterval);
    }
  }, [paymentStatus, qrData, checkPaymentStatus]);

  const handleClose = () => {
    if (paymentStatus === 'completed') {
      onClose();
    } else {
      // Ask for confirmation before closing
      if (window.confirm('Pembayaran belum selesai. Yakin ingin menutup?')) {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[80%] max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-primary" />
            Scan QR Code untuk Pembayaran
          </DialogTitle>
          <DialogDescription>
            Scan menggunakan aplikasi mobile banking atau e-wallet kamu
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground">Membuat QR Code...</p>
            </div>
          )}

          {error && (
            <Alert className="border-red-500/50 bg-red-500/10">
              <XCircle className="h-5 w-5 text-red-500" />
              <AlertDescription className="text-sm ml-2">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {qrData && paymentStatus !== 'completed' && !error && (
            <>
              <div className="flex justify-center p-6 bg-white rounded-lg">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrData.payment_number)}`}
                  alt="QR Code"
                  className="w-64 h-64"
                />
              </div>

              <div className="space-y-2 p-4 rounded-lg border border-border bg-muted/30">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Order ID:</span>
                  <span className="font-mono font-semibold">{qrData.order_id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Pembayaran:</span>
                  <span className="font-semibold text-lg text-primary">
                    Rp {qrData.total_payment?.toLocaleString('id-ID') || '0'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Metode:</span>
                  <span className="font-semibold uppercase">{qrData.payment_method}</span>
                </div>
              </div>

              {timeLeft && timeLeft !== 'Expired' && (
                <div className="flex items-center gap-3 p-4 rounded-lg border border-blue-500/30 bg-blue-500/5">
                  <Clock className="h-5 w-5 text-blue-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">QR Code akan expired dalam:</p>
                    <p className="text-lg font-bold text-blue-500 font-mono">{timeLeft}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 p-4 rounded-lg border border-primary/30 bg-primary/5">
                <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground mb-2">Cara Pembayaran:</p>
                  <ol className="space-y-1 text-xs text-muted-foreground list-decimal list-inside">
                    <li>Buka aplikasi mobile banking atau e-wallet</li>
                    <li>Pilih menu Scan QR/QRIS</li>
                    <li>Scan QR Code di atas</li>
                    <li>Konfirmasi dan lakukan pembayaran</li>
                    <li>Tunggu hingga pembayaran terverifikasi</li>
                  </ol>
                </div>
              </div>
            </>
          )}

          {paymentStatus === 'completed' && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Pembayaran Berhasil!</h3>
              <p className="text-sm text-muted-foreground text-center">
                Benefit akan segera diberikan ke akun kamu.
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="flex justify-center">
            <Button onClick={generateQRCode}>
              Coba Lagi
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
